import React from 'react';
import fhitpath from 'fhirpath';

import LoadingSwitchTransition from '@/components/LoadingSwitchTransition';
import PlaceholderFlatListProtocolo from '@/features/dashboard/components/PlaceholderFlatListProtocolo';
import orderBy from 'lodash.orderby';
import { FiAlertCircle } from 'react-icons/fi';
import { MdAccessTime, MdCheck } from 'react-icons/md';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import { useSolicitacoesConsolidadas } from '@/features/execucao/hooks/useSolicitacoesConsolidadas';
import { SectionResumoType } from '../../models/types';
import { Container, Content, Item, ItemIcon, ItemTitle, Title } from './styles';
import { useEnterpriseContext } from '../../contexts/EnterpriseContext';

const DadosSumario: React.FC = () => {
  const [{ bundle }] = useEnterpriseContext();
  const [{ protocoloExecutado }] = useExecucaoContext();
  const { solicitacoes } = useSolicitacoesConsolidadas();

  const isLoading = React.useMemo(
    () =>
      protocoloExecutado?.contexto_integracao?.status_integracao ===
        'on-hold' && bundle.status === 'on-hold',
    [bundle.status, protocoloExecutado?.contexto_integracao?.status_integracao],
  );

  const hasMedicationRequest = React.useMemo(
    () =>
      fhitpath.evaluate(
        bundle.data,
        "entry.resource.where(resourceType='MedicationRequest').exists()",
      )[0],
    [bundle],
  );

  const hasServiceRequest = React.useMemo(
    () =>
      fhitpath.evaluate(
        bundle.data,
        "entry.resource.where(resourceType='ServiceRequest').exists()",
      )[0],
    [bundle],
  );

  const hasProcedimento = React.useMemo(
    () => solicitacoes.some(s => s.tipo === 'procedimento'),
    [solicitacoes],
  );

  const secoes = React.useMemo<SectionResumoType[]>(() => {
    if (!protocoloExecutado?.sumario) return [];

    const sorted = orderBy(
      protocoloExecutado?.sumario,
      ['display_block', 'ordem'],
      ['desc', 'asc'],
    ).filter(item => {
      if (item.modelo === 'solicitacoes-exame') {
        return hasServiceRequest;
      }
      if (item.modelo === 'solicitacoes-medicamento') {
        return hasMedicationRequest;
      }
      if (item.modelo === 'solicitacoes-procedimento') {
        return hasProcedimento;
      }

      return true;
    });

    return sorted;
  }, [
    hasMedicationRequest,
    hasServiceRequest,
    hasProcedimento,
    protocoloExecutado?.sumario,
  ]);

  const hasSumario = React.useMemo(() => !!secoes.length, [secoes]);

  const noneRendered = React.useMemo(
    () => secoes.every(item => !item.texto_renderizado),
    [secoes],
  );

  const ContentWarning = React.useMemo(() => {
    if (!hasSumario) {
      return (
        <Item active>
          <ItemIcon>
            <FiAlertCircle size={20} />
          </ItemIcon>
          <ItemTitle>Este protocolo não gera resumo</ItemTitle>
        </Item>
      );
    }
    if (!protocoloExecutado?.executado) {
      return (
        <Item>
          <ItemIcon>
            <MdAccessTime size={20} />
          </ItemIcon>
          <ItemTitle>Em andamento</ItemTitle>
        </Item>
      );
    }

    return <></>;
  }, [hasSumario, protocoloExecutado?.executado]);

  const ContentDadosSumario = React.useMemo(() => {
    const ContentSecoes = secoes.map(section => {
      if (section.modelo === 'solicitacoes-exame' && !noneRendered) {
        return (
          <Item active key={section.codigo}>
            <ItemIcon>
              <MdCheck size={20} />
            </ItemIcon>
            <ItemTitle>Exames Prescritos</ItemTitle>
          </Item>
        );
      }

      if (section.modelo === 'solicitacoes-medicamento' && !noneRendered) {
        return (
          <Item active key={section.codigo}>
            <ItemIcon>
              <MdCheck size={20} />
            </ItemIcon>
            <ItemTitle>Medicamentos Prescritos</ItemTitle>
          </Item>
        );
      }

      if (section.modelo === 'solicitacoes-procedimento' && !noneRendered) {
        return (
          <Item active key={section.codigo}>
            <ItemIcon>
              <MdCheck size={20} />
            </ItemIcon>
            <ItemTitle>Procedimentos Solicitados</ItemTitle>
          </Item>
        );
      }

      return (
        !!section.texto_renderizado && (
          <Item active key={section.codigo}>
            <ItemIcon>
              <MdCheck size={20} />
            </ItemIcon>
            <ItemTitle>{section.titulo}</ItemTitle>
          </Item>
        )
      );
    });

    return (
      <>
        {ContentSecoes}
        {ContentWarning}
      </>
    );
  }, [ContentWarning, noneRendered, secoes]);

  return (
    <LoadingSwitchTransition
      loading={isLoading}
      LoadingComponent={<PlaceholderFlatListProtocolo />}
      style={{ width: '100%' }}
    >
      <Container>
        <Title>Resumo do Atendimento</Title>
        <Content>{ContentDadosSumario}</Content>
      </Container>
    </LoadingSwitchTransition>
  );
};

export default DadosSumario;
